import {
    CloudStorageType,
    FileGenerationConnection,
    GeneratedFileStatus,
} from "@biggeo/bg-server-lib/studio";
import { GridColDef } from "@biggeo/bg-ui";
import {
    AvatarGroup,
    Button,
    ColFilterType,
    DataGridContainer,
    Grid,
    IconAvatar,
    IFilterSearchPaginate,
    MemoizedField,
    Stack,
    Tag,
    ThumbnailAvatar,
    thumbnailAvatarClasses,
    Typography,
} from "@biggeo/bg-ui/lab";
import {
    ArrowSplitOutline,
    Background,
    CheckCircleOutline,
    ErrorOutline,
    ProgressActivityOutline,
    PublicOutline,
    ScheduleOutline,
    UploadOutline,
} from "@biggeo/bg-ui/lab/icons";
import {
    formatDateMMMDYYYY,
    logoMap,
} from "@biggeo/bg-utils";

import compact from "lodash/compact";
import { useNavigate } from "react-router";
import { match } from "ts-pattern";
import { Routes } from "../../navigation/redux/model";
import { formatTreeData, getFileGenerationLastExportedAt, getFileGenerationStatus, isParentRow, isChildRow } from "../utils/utils";

interface IGeneratedFilesView {
    data: readonly FileGenerationConnection[];
    pagination: IFilterSearchPaginate;
    loading: boolean;
}

export type TreeDataRow = {
    id: string;
    source: CloudStorageType[]
    name: string;
    description?: string;
    status: GeneratedFileStatus;
    size?: bigint;
    fileCount: number;
    savedViewId?: string;
    mapTemplateId?: string;
    lastExportedAt?: Date;
    generatedFileId?: string;
    _type: RowType;
}
export enum RowType {
    parent = "parent",
    child = "child"
}

const GeneratedFilesView = ({
    data,
    pagination,
    loading,
}: IGeneratedFilesView) => {
    const toPage = useNavigate();
    const openNewTab = (url: string) => {
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const formatter = Intl.NumberFormat("en", { notation: "compact" });
    const rowData = formatTreeData(data);
    const columns: GridColDef<TreeDataRow>[] = [
        {
            field: "source",
            headerName: "Source",
            width: 110,
            sortable: false,
            filterable: false,
            headerAlign: "center",
            align: "center",
            renderCell: ({ row }) => {
                return (
                    <MemoizedField>
                        <AvatarGroup
                            max={3}
                            size="xxs"
                        >
                            {row.source?.map(storageType => {
                                return match(storageType)
                                    .with(CloudStorageType.Upload, () => {
                                        return (
                                            <IconAvatar
                                                square
                                                size="sm"
                                                color="background"
                                                sx={{
                                                    border: 1,
                                                    borderColor: (theme) =>
                                                        theme.palette.stroke[100],
                                                }}
                                            >
                                                <UploadOutline />
                                            </IconAvatar>
                                        );
                                    })
                                    .otherwise(() => {
                                        return (
                                            <ThumbnailAvatar
                                                color="surface"
                                                size="sm"
                                                sx={{
                                                    borderRadius: (theme) =>
                                                        theme.radius.default,
                                                    border: 1,
                                                    borderColor: (theme) =>
                                                        theme.palette.stroke[100],
                                                    [`& .${thumbnailAvatarClasses.image}`]: {
                                                        backgroundColor: (theme) =>
                                                            theme.palette.background.main,
                                                    },
                                                }}
                                                src={
                                                    storageType &&
                                                        storageType !== CloudStorageType.Upload
                                                        ? logoMap[storageType]
                                                        : undefined
                                                }
                                                alt={storageType}
                                            />
                                        );
                                    })
                            }
                            )}
                        </AvatarGroup>
                    </MemoizedField>
                );
            },
        },
        {
            field: "name",
            headerName: "Name",
            minWidth: 280,
            flex: 1,
            sortable: false,
            type: ColFilterType.string,
            filterable: true,
            headerAlign: "left",
            align: "center",
            renderCell: (params) => (
                <MemoizedField
                    title={params.row.name}
                    subTitle={params.row.description || undefined}
                />
            ),
        },
        {
            field: "fileCount",
            headerName: "File Generated",
            flex: 1,
            minWidth: 180,
            filterable: false,
            sortable: false,
            headerAlign: "left",
            align: "center",

            renderCell: ({ row }) => {
                const isRunning = row.status === GeneratedFileStatus.queued || row.status === GeneratedFileStatus.running;
                return (
                    <MemoizedField
                        sx={{
                            justifyContent: "flex-start"
                        }}
                    >
                        <Tag
                            density="dense"
                            variant="tonal"
                            sx={{ backgroundColor: "transparent" }}
                            startNode={
                                <AvatarGroup
                                    max={3}
                                    size="xxs"
                                >
                                    {Array.from({ length: row.fileCount }, (_, i) => (
                                        <Background key={i} />
                                    ))}

                                    {isChildRow(row) && isRunning
                                        ? <ProgressActivityOutline size="sm" color="success" />
                                        : null
                                    }
                                </AvatarGroup>
                            }

                        >
                            {isParentRow(row)
                                ? "Files"
                                : isRunning
                                    ? "Generating..."
                                    : <Button
                                        variant="outlined"
                                        rounded
                                        endNode={<ArrowSplitOutline size="xs" />}
                                        onClick={() => {
                                            row.generatedFileId && toPage(
                                                `${Routes.myData}/export/${row.generatedFileId}`
                                            );
                                        }}
                                        disabled={row.status === GeneratedFileStatus.failed || row.status === GeneratedFileStatus.expired}
                                    >
                                        Export
                                    </Button>
                            }
                        </Tag>

                    </MemoizedField>
                );
            },
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            minWidth: 120,
            filterable: true,
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderCell: ({ row }) => {
                const status = row.status;
                return (
                    <MemoizedField>
                        <Grid
                            container
                            spacing={1}
                            alignItems="center"
                            justifyContent="center"
                            sx={{
                                padding: 1,
                            }}
                        >
                            <Grid item>
                                {match(status)
                                    .with(GeneratedFileStatus.completed, () => (
                                        <CheckCircleOutline
                                            color="success"
                                            size="xxs"
                                        />
                                    ))
                                    .with(GeneratedFileStatus.failed, () => (
                                        <ErrorOutline
                                            color="error"
                                            size="xxs"
                                        />
                                    ))
                                    .with(GeneratedFileStatus.queued, () => (
                                        <ScheduleOutline
                                            color="warning"
                                            size="xxs"
                                        />
                                    ))
                                    .with(GeneratedFileStatus.running, () => (
                                        <ScheduleOutline
                                            color="warning"
                                            size="xxs"
                                        />
                                    ))
                                    .with(GeneratedFileStatus.expired, () => (
                                        <ScheduleOutline
                                            color="disabled"
                                            size="xxs"
                                        />
                                    ))
                                    .otherwise(() => (
                                        <></>
                                    ))}
                            </Grid>
                            <Grid item>
                                <Typography
                                    variant="body3"
                                    fontWeight="semibold"
                                >
                                    {match(status)
                                        .with(GeneratedFileStatus.completed, () => "Completed")
                                        .with(GeneratedFileStatus.failed, () => "Failed")
                                        .with(GeneratedFileStatus.queued, () => "Queued")
                                        .with(
                                            GeneratedFileStatus.running,
                                            () => "Running"
                                        )
                                        .with(GeneratedFileStatus.expired, () => "Expired")
                                        .otherwise(() => "-")}
                                </Typography>
                            </Grid>

                        </Grid>
                    </MemoizedField>
                );
            },
        },
        {
            field: "size",
            headerName: "Size",
            flex: 1,
            minWidth: 100,
            filterable: true,
            type: ColFilterType.number,
            sortable: false,
            headerAlign: "left",
            align: "center",
            renderCell: ({ row }) => {
                if (
                    isParentRow(row)
                ) {
                    return (
                        <MemoizedField
                            title={"-"}
                        />
                    );
                }
                return (
                    <MemoizedField
                        sx={{
                            justifyContent: "flex-start"
                        }}
                        title={`${formatter.format(row.size || 0)}`}
                    />
                );
            },
        },
        {
            field: "savedViewId",
            headerName: "Map",
            flex: 1,
            minWidth: 100,
            filterable: false,
            sortable: false,
            headerAlign: "center",
            align: "center",
            renderCell: ({ row }) => {
                return (
                    <MemoizedField>
                        {isParentRow(row)
                            ? (<Button
                                variant="outlined"
                                endNode={<PublicOutline size="xs" />}
                                onClick={() => {
                                    row.mapTemplateId && row.savedViewId && toPage(
                                        `${Routes.mapView}/${row.mapTemplateId}/map?savedViewId=${row.savedViewId}`
                                    )
                                }}
                            >
                                View Map
                            </Button>)
                            : <></>
                        }
                    </MemoizedField>
                );
            },
        },
        {
            field: "lastExportedAt",
            headerName: "Last Export",
            flex: 1,
            minWidth: 140,
            sortable: false,
            filterable: false,
            type: ColFilterType.string,
            headerAlign: "center",
            align: "center",
            renderCell: ({ row }) => {

                return (
                    <Grid container alignItems="center" justifyContent="center">
                        <Typography variant="body3" fontWeight="semibold">
                            {row.lastExportedAt ? formatDateMMMDYYYY(row.lastExportedAt) : "-"}
                        </Typography>
                    </Grid>
                );
            },
        },
    ];

    return (
        <Stack
            height="100%"
            width="100%"
            sx={{
                paddingTop: 4,
            }}
            gap={4}
        >
            <DataGridContainer
                loading={loading}
                columns={columns}
                rows={rowData}
                rowCount={rowData.length}
                pagination
                filterSearchPaginateProps={pagination}
                treeData
                getTreeDataPath={(row) => row.id.split("/")}
                groupingColDef={{
                    headerName: "",
                    width: 58,
                }}
            />
        </Stack>
    );
};

export default GeneratedFilesView;
